'use client'

import { useFormik } from 'formik'
import { useRouter } from 'next/navigation'
import React, { useCallback, useEffect } from 'react'

import { ChangeBankAccount, ChangeProfileInfo } from '..'

import usePostAddBankAccountMutation, {
  DEFAULT_EWALLET,
} from '@/libs/apis/mutations/use-post-add-bank-account-mutation'
import { useGetClientConfigQuery } from '@/libs/apis/queries/use-get-client-config-query'
import { getBankOptions } from '@/libs/helpers/payout-whitelist-filter'
import { useAppSelector } from '@/libs/hooks/use-app-selector'
import useGetParams from '@/libs/hooks/use-get-params'
import useToast from '@/libs/hooks/use-toast'
import validateBankAccountForm from '@/libs/logics/validate-bank-account-form'
import { EApiErrorCode, EPayoutType } from '@/libs/types/global.types'

export type TPayoutInfoParams = 'bank-account' | 'e-wallet'
type TAddPayoutInfoForm = { readonly type: TPayoutInfoParams }

export default function AddPayoutInfoForm({ type }: TAddPayoutInfoForm) {
  const { mutate, isPending } = usePostAddBankAccountMutation()
  const { source } = useGetParams()
  const { push } = useRouter()
  const { partnerId } = useAppSelector((state) => state.user)
  const toast = useToast()
  const isTypeBank = type === 'bank-account'
  const isTypeWallet = type === 'e-wallet'
  const formik = useFormik({
    initialValues: {
      type: EPayoutType.BANK,
      provider: '',
      accountNumber: '',
      accountName: '',
    },
    enableReinitialize: true,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: false,
    validate: (values) => validateBankAccountForm(values, type),
    onSubmit: (values) => {
      mutate(values, {
        onSuccess: () => {
          const toastMessage = isTypeBank ? 'Add Bank Account Success' : 'Add E-Wallet Success'

          if (source && source === 'select_withdraw_method') {
            return push('/rewards/select-withdraw-method')
          }

          toast.show({
            title: toastMessage,
            description: '',
            type: 'success',
          })

          return push(`/profile`)
        },
        onError: ({ error, traceId }: any) => {
          if (error) {
            if (error?.code === EApiErrorCode.DUPLICATE_PAYMENT_INFO) {
              formik.setFieldError('accountNumber', error?.message)
            } else {
              toast.show({
                type: 'error',
                title: error?.code,
                description: error?.message,
                traceId,
              })
            }
          }
        },
      })
    },
  })

  useEffect(() => {
    if (isTypeBank) formik.setFieldValue('type', EPayoutType.BANK)
    if (isTypeWallet) {
      formik.setFieldValue('type', EPayoutType.EWALLET)
      formik.setFieldValue('provider', DEFAULT_EWALLET)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type])

  const { data: dataClientQuery } = useGetClientConfigQuery({ clientId: partnerId as string })

  const bankOptions = getBankOptions({
    partner: !!partnerId,
    whitelist: dataClientQuery?.result?.withdrawMethodWhitelist,
  })

  const renderAddPayoutPage = useCallback(() => {
    switch (type) {
      case 'bank-account':
        return (
          <ChangeBankAccount
            providerValue={formik.values.provider}
            onChangeProvider={(value: string) => formik.setFieldValue('provider', value)}
            errorProvider={formik.errors.provider}
            value={formik.values.accountNumber}
            onChangeValue={(value: string) => formik.setFieldValue('accountNumber', value)}
            error={formik.errors.accountNumber}
            buttonLabel='Save'
            isButtonDisabled={isPending}
            bankOptions={bankOptions}
          />
        )
      case 'e-wallet':
        return (
          <ChangeProfileInfo
            value={formik.values.accountNumber}
            onChangeValue={(value: string) => formik.setFieldValue('accountNumber', value)}
            error={formik.errors.accountNumber}
            buttonLabel='Submit'
            inputLabel='Enter your phone number'
            inputCaption='Phone Number'
            isButtonDisabled={isPending}
            inputType='tel'
          />
        )
    }
  }, [bankOptions, formik, isPending, type])

  return <form onSubmit={formik.handleSubmit}>{renderAddPayoutPage()}</form>
}
